export interface PermissionRecord {
  id: number;
  name: string;
}

export interface PermissionGroup {
  titleKey: string;
  permissions: PermissionRecord[];
  viewPermission?: PermissionRecord;
}

const PERMISSION_VERB_REGEX = /^(view|create|edit|delete|manage|approve)\s+/;

const PERMISSION_GROUP_TRANSLATION_KEYS: Record<string, string> = {
  'tablet app content': 'permissionGroupTabletAppContent',
  dashboard: 'permissionGroupDashboard',
  users: 'permissionGroupUsers',
  roles: 'permissionGroupRoles',
  employees: 'permissionGroupEmployees',
  clients: 'permissionGroupClients',
  equipment: 'permissionGroupEquipment',
  inventory: 'permissionGroupInventory',
  vehicles: 'permissionGroupVehicles',
  'rental suppliers': 'permissionGroupRentalSuppliers',
  branches: 'permissionGroupBranches',
  jobs: 'permissionGroupJobs',
  'job equipment': 'permissionGroupJobEquipment',
  'job documents': 'permissionGroupJobDocuments',
  'job notes': 'permissionGroupJobNotes',
  'job damage and loss reports': 'permissionGroupJobDamageAndLossReports',
  'job print-outs': 'permissionGroupJobPrintOuts',
  'job additional costs': 'permissionGroupJobAdditionalCosts',
  'job cost overview': 'permissionGroupJobCostOverview',
  'job financial documents': 'permissionGroupJobFinancialDocuments',
  'work orders': 'permissionGroupWorkOrders',
  'work in progress reports': 'permissionGroupWorkInProgressReports',
};

const PERMISSION_ORDER_PRIORITY: Record<string, number> = {
  view: 0,
  create: 1,
  edit: 2,
  delete: 4,
};

const permissionVerb = (name: string): string => {
  const match = name.match(PERMISSION_VERB_REGEX);
  return match ? match[1] : '';
};

const sortGroupPermissions = (permissions: PermissionRecord[]): PermissionRecord[] => {
  return [...permissions].sort((a, b) => {
    const priorityA = PERMISSION_ORDER_PRIORITY[permissionVerb(a.name)] ?? 3;
    const priorityB = PERMISSION_ORDER_PRIORITY[permissionVerb(b.name)] ?? 3;
    return priorityA - priorityB;
  });
};

export const permissionTranslationKey = (name: string): string => {
  const pascalCase = name
    .replace(/-/g, ' ')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  return `permission${pascalCase}`;
};

export const groupPermissions = (permissionsList: PermissionRecord[]): PermissionGroup[] => {
  const groups: Record<string, PermissionRecord[]> = {};

  permissionsList.forEach((permission) => {
    let category = permission.name.replace(PERMISSION_VERB_REGEX, '').trim();
    if (category === 'role') category = 'roles';
    if (category.startsWith('dashboard')) category = 'dashboard';
    if (category === 'roles of users' || category === 'branches of users') category = 'users';

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(permission);
  });

  return Object.keys(groups).map(category => {
    const sortedPermissions = sortGroupPermissions(groups[category]);

    return {
      titleKey: PERMISSION_GROUP_TRANSLATION_KEYS[category]
        ?? category.charAt(0).toUpperCase() + category.slice(1),
      permissions: sortedPermissions,
      // The dashboard group has multiple independent "view dashboard ..." permissions
      // rather than one view permission gating others, so it's excluded from that coupling.
      viewPermission: category === 'dashboard'
        ? undefined
        : sortedPermissions.find((permission) => /^view\s+/.test(permission.name)),
    };
  });
};
